import {AnyAction} from 'redux';

// Thin compatibility layer to replace `redux-first-history` action creators.
// Provides the same names (push, replace, go, goBack, goForward) so existing code can keep dispatching.

/** Action type used by the shared navigation bridge for `push`. */
export const navigationPush = 'navigation/PUSH';
/** Action type used by the shared navigation bridge for `replace`. */
export const navigationReplace = 'navigation/REPLACE';
/** Action type used by the shared navigation bridge for `go`. */
export const navigationGo = 'navigation/GO';
/** Action type used by the shared navigation bridge for `goBack`. */
export const navigationGoBack = 'navigation/GO_BACK';
/** Action type used by the shared navigation bridge for `goForward`. */
export const navigationGoForward = 'navigation/GO_FORWARD';

/** Push navigation action consumed by the router middleware. */
export interface PushAction extends AnyAction {
    type: typeof navigationPush;
    payload: {
        path: string,
        state?: unknown,
    };
}

/** Replace navigation action consumed by the router middleware. */
export interface ReplaceAction extends AnyAction {
    type: typeof navigationReplace;
    payload: {
        path: string,
        state?: unknown,
    };
}

/** Relative navigation action consumed by the router middleware. */
export interface GoAction extends AnyAction {
    type: typeof navigationGo;
    payload: number;
}

/** Back navigation action consumed by the router middleware. */
export interface GoBackAction extends AnyAction {
    type: typeof navigationGoBack;
}

/** Forward navigation action consumed by the router middleware. */
export interface GoForwardAction extends AnyAction {
    type: typeof navigationGoForward;
}

/** Union of all router bridge actions handled by the shell middleware. */
export type NavigationAction = PushAction | ReplaceAction | GoAction | GoBackAction | GoForwardAction;

/**
 * Creates a navigation action that pushes a new route onto history.
 *
 * @param path Target path to push.
 * @param state Optional history state payload.
 * @returns Navigation action for the middleware bridge.
 *
 * @example
 * push('/home');
 */
export const push = (path: string, state?: unknown): PushAction => ({
    type: navigationPush,
    payload: {path, state},
});

/**
 * Creates a navigation action that replaces the current history entry.
 *
 * @param path Target path to replace with.
 * @param state Optional history state payload.
 * @returns Navigation action for the middleware bridge.
 *
 * @example
 * replace('/home');
 */
export const replace = (path: string, state?: unknown): ReplaceAction => ({
    type: navigationReplace,
    payload: {path, state},
});

/**
 * Creates a navigation action that moves by the provided history delta.
 *
 * @param delta Relative history offset.
 * @returns Navigation action for the middleware bridge.
 *
 * @example
 * go(-1);
 */
export const go = (delta: number): GoAction => ({
    type: navigationGo,
    payload: delta,
});

/**
 * Creates a navigation action that moves one step back in history.
 *
 * @returns Navigation action for the middleware bridge.
 *
 * @example
 * goBack();
 */
export const goBack = (): GoBackAction => ({
    type: navigationGoBack,
});

/**
 * Creates a navigation action that moves one step forward in history.
 *
 * @returns Navigation action for the middleware bridge.
 *
 * @example
 * goForward();
 */
export const goForward = (): GoForwardAction => ({
    type: navigationGoForward,
});
